<template>
<main class="bg-gray-100 pb-10 min-h-screen">
  <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
    <p v-show="!formState.loadError && !formState.registration" class="text-gray-600 text-center mt-10">
      Loading registration details, please wait...
    </p>

    <ErrorMessage :formError="formState.loadError" class="mt-4" />

    <!-- Back to List -->
    <div v-if="eventIdList > 0" class="mt-6">
      <a :href="'/registration-ticket/list/' + eventIdList" class="text-blue-600 hover:underline flex items-center gap-1">
        <span class="text-xl">&larr;</span> <span>Back to List</span>
      </a>
    </div>

    <!-- Checkout Info -->
    <div v-if="formState.registration?.checkout" class="mt-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
        Checkout Info
        <span class="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded font-mono">Order #{{ formState.registration.checkout[0].checkoutId }}</span>
      </h3>
      <div class="bg-white border border-gray-200 rounded-lg shadow p-4 space-y-1">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p class="text-base font-bold">{{ formState.registration.checkout[0].firstName }} {{ formState.registration.checkout[0].lastName }}</p>
            <p class="text-sm text-gray-700">{{ formState.registration.checkout[0].email }}</p>
            <p v-if="formState.registration.checkout[0].tel" class="text-sm text-gray-700">Tel: {{ formState.registration.checkout[0].tel }}</p>
          </div>
          <div>
            <p class="text-xs font-bold text-gray-500">Order ID</p>
            <p class="text-xs text-gray-700 font-mono break-all">{{ formState.registration.checkout[0].checkoutId }}</p>
          </div>
        </div>
        <div class="flex flex-wrap gap-4 mt-1">
          <div v-if="formState.registration.checkout[0].confirmedAt">
            <p class="text-xs font-bold text-green-700">Confirmed At</p>
            <p class="text-xs text-gray-700">{{ $filters.dateLon(formState.registration.checkout[0].confirmedAt) }}</p>
          </div>
          <div v-if="formState.registration.checkout[0].cancelledAt">
            <p class="text-xs font-bold text-red-500">Cancelled At</p>
            <p class="text-xs text-gray-700">{{ $filters.dateLon(formState.registration.checkout[0].cancelledAt) }}</p>
          </div>
          <div v-if="formState.registration.checkout[0].isPartner">
            <p class="text-xs font-bold text-green-600">SPECIFIED AS PARTNER</p>
          </div>
          <div v-if="formState.registration.checkout[0].country?.toLowerCase() !== 'gb'">
            <p class="text-xs font-bold text-blue-600">🌍 {{ formState.registration.checkout[0].country }} - INTERNATIONAL</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Delegates -->
    <div v-if="formState.registration?.delegates" class="mt-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-2">Delegates</h3>
      <div class="bg-white border border-gray-200 rounded-lg shadow p-2 sm:p-4">
        <div
          v-for="(item) in formState.registration.delegates"
          :key="item.id"
          @click.prevent="changeRegistered(item)"
          class="cursor-pointer hover:bg-blue-50 px-2 py-3 rounded-md transition flex flex-row items-center gap-3 border-b border-gray-200 last:border-b-0"
        >
          <!-- Check-in/out icon -->
          <span class="flex-shrink-0">
            <CheckCircleIcon v-if="item.registered" class="h-8 w-8 text-green-500" />
            <XMarkIcon v-else class="h-8 w-8 text-red-400" />
          </span>
          <!-- Delegate info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span :class="[item.cancelled ? 'line-through text-gray-400' : '', 'font-bold text-base']">
                {{ item.firstName }} {{ item.lastName }}
                <span v-if="item.cancelled" class="ml-2 text-xs font-bold text-red-500">CANCELLED</span>
              </span>
            </div>
            <div class="text-xs text-gray-600 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span v-if="item.ticketTitle" class="block w-full sm:w-auto">{{ item.ticketTitle }}</span>
              <span v-if="item.child" class="inline-block bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Child</span>
              <span v-if="item.child" class="ml-1">Age: {{ item.age }}</span>
              <span v-if="item.child && item.gender" class="ml-1">
                / {{ item.gender === 'F' ? 'Girl' : item.gender === 'M' ? 'Boy' : item.gender }}
              </span>
              <span v-if="item.colour" class="inline-flex items-center ml-1">
                <span class="text-sm font-bold">{{ item.colour }}</span>
                <span v-if="item.colourCode" class="text-sm font-bold ml-1">({{ item.colourCode }})</span>
              </span>
              <span v-if="item.wristband" class="ml-1 text-orange-500 font-semibold">Band: {{ item.wristband }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Attendance Options -->
    <div v-if="formState.registration?.attendanceOptions" class="mt-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-2">Session Attendance</h3>
      <div class="bg-white border border-gray-200 rounded-lg shadow p-2 sm:p-4 space-y-2">
        <div
          v-for="option in formState.registration.attendanceOptions"
          :key="option.id"
          class="border-b border-gray-100 pb-3 last:border-b-0"
        >
          <div class="flex items-start text-sm text-gray-700">
            <component
              :is="option.attending == null ? 'QuestionMarkCircleIcon' : (option.attending ? 'CheckIcon' : 'XMarkIcon')"
              :class="option.attending == null ? 'text-red-500' : option.attending ? 'text-green-500' : 'text-red-500'"
              class="h-5 w-5 mr-2 flex-shrink-0"
            />
            <span>
              {{ option.title }} - {{ $filters.dateTimeMed(option.publishedAt) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</main>
</template>

<script>
import axios from 'axios'
import { defineComponent, onMounted, computed, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import responseHelper from '@/helpers/response.helper'
// import SuccessMessage from '@/components/SuccessMessage.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import { CheckCircleIcon, XMarkIcon, CheckIcon, QuestionMarkCircleIcon } from '@heroicons/vue/20/solid'

export default defineComponent({
  components: {
    // SuccessMessage,
    ErrorMessage,
    CheckCircleIcon,
    XMarkIcon,
    QuestionMarkCircleIcon,
    CheckIcon
  },
  setup () {
    const route = useRoute()
    const registrationGuid = ref(route.params.guid)

    const formState = reactive({
      registration: undefined,
      loadError: responseHelper.noError,
      formSubmitted: false,
      formError: responseHelper.noError,
      successMessage: ''
    })

    const changeRegistered = async (item) => {
      axios.post(
        'pub/registrations/register',
        {
          guid: registrationGuid.value,
          delegateId: item.id
        }
      ).then((response) => {
        if (response?.data?.success && response?.data?.result) {
          formState.registration = response.data.result
        } else {
          formState.loadError = responseHelper.unexpectedResult
        }
      }).catch((error) => {
        formState.loadError = responseHelper.getMessageV2(error)
      })
    }

    onMounted(() => {
      axios.post(
        'pub/registrations/kids',
        {
          guid: registrationGuid.value
        }
      ).then((response) => {
        if (response?.data?.success && response?.data?.result) {
          formState.registration = response.data.result
          console.log('Registration data loaded:', formState.registration)
        } else {
          formState.loadError = responseHelper.unexpectedResult
        }
      }).catch((error) => {
        formState.loadError = responseHelper.getMessageV2(error)
      })
    })

    const eventIdList = computed(() => {
      var eventId = 0
      if (formState && formState.registration && formState.registration.delegates.length > 0) {
        for (var i = 0; i < formState.registration.delegates.length; i++) {
          eventId = formState.registration.delegates[i].eventId
        }
      }
      return eventId
    })

    return {
      formState,
      registrationGuid,
      changeRegistered,
      eventIdList
    }
  }
})
</script>
